const DEFAULT_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:4200';

export const TRACKING_API_BASE_URL = (
  process.env.REACT_APP_TRACKING_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  DEFAULT_API_BASE_URL
).replace(/\/+$/, '');

/**
 * Browser-exposed lookup key for POST /api/v1/production-tracking/lookup.
 * This value is visible in developer tools by design for the direct browser
 * integration path — rotate it if leaked and prefer a short-lived gateway later.
 */
export function getTrackingApiSecurityKey() {
  return process.env.REACT_APP_TRACKING_SECURITY_KEY || '';
}

export class TrackingApiError extends Error {
  constructor(message, { code = 'TRACKING_REQUEST_FAILED', status = 0, requestId = null } = {}) {
    super(message);
    this.name = 'TrackingApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

export async function lookupShipment(trackingNumber, {
  signal,
  shareToken,
  shipraAccessTimestamp,
  shipraAccessSignature,
  timeoutMs = 15_000,
} = {}) {
  let response;
  const timeoutController = signal ? null : new AbortController();
  const requestSignal = signal || timeoutController.signal;
  const timeoutId = timeoutController
    ? setTimeout(() => timeoutController.abort(), timeoutMs)
    : null;

  try {
    const normalizedTrackingNumber = trackingNumber.trim();

    // Share-token deep links remain supported when the API exposes them.
    if (shareToken) {
      const url = `${TRACKING_API_BASE_URL}/api/v1/tracking/share/${encodeURIComponent(shareToken)}` +
        `?trackingNumber=${encodeURIComponent(normalizedTrackingNumber)}`;
      response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        signal: requestSignal,
      });
    }
    else {
      if (!getTrackingApiSecurityKey()) {
        throw new TrackingApiError(
          'Tracking security key is not configured. Set REACT_APP_TRACKING_SECURITY_KEY.',
          { code: 'TRACKING_SECURITY_KEY_MISSING' },
        );
      }

      response = await fetch(`${TRACKING_API_BASE_URL}/api/v1/production-tracking/lookup`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${getTrackingApiSecurityKey()}`,
        },
        signal: requestSignal,
        body: JSON.stringify({
          trackingNumber: normalizedTrackingNumber,
          ...(shipraAccessTimestamp && shipraAccessSignature ? {
            shipraAccessTimestamp: Number(shipraAccessTimestamp),
            shipraAccessSignature,
          } : {}),
        }),
      });
    }
  }
  catch (error) {
    if (error instanceof TrackingApiError)
      throw error;
    if (error?.name === 'AbortError') {
      if (signal)
        throw error;
      throw new TrackingApiError('The tracking request timed out. Please try again.', {
        code: 'TRACKING_REQUEST_TIMEOUT',
      });
    }

    throw new TrackingApiError(
      'The tracking service is temporarily unavailable. Please try again shortly.',
      { code: 'TRACKING_SERVICE_UNAVAILABLE' },
    );
  }
  finally {
    if (timeoutId)
      clearTimeout(timeoutId);
  }

  let payload = null;
  try {
    payload = await response.json();
  }
  catch {
    // Non-JSON proxy/server responses are handled below.
  }

  if (!response.ok || !payload?.data) {
    throw new TrackingApiError(
      payload?.error?.message || 'Unable to load tracking details. Please try again.',
      {
        code: payload?.error?.code,
        status: response.status,
        requestId: payload?.error?.requestId || payload?.requestId,
      },
    );
  }

  return normalizeTrackingLookup(payload.data);
}

/**
 * Accepts either the production lookup envelope or a pre-normalized shipment
 * object (used by tests / older fixtures).
 */
export function normalizeTrackingLookup(data) {
  if (!data || typeof data !== 'object')
    return data;

  if (data.trackingInfo || data.shipmentTimeline || data.shipmentProgress) {
    const info = data.trackingInfo || {};
    const map = data.shipmentMap || {};
    const timeline = Array.isArray(data.shipmentTimeline) ? data.shipmentTimeline : [];
    const progress = Array.isArray(data.shipmentProgress) ? data.shipmentProgress : [];
    const hasTrackingNumber = Boolean(info.trackingNumber);
    const mappedCurrentStatus = info.currentStatus?.code
      ? {
        code: info.currentStatus.code ?? null,
        name: info.currentStatus.name ?? null,
        category: info.currentStatus.category ?? null,
        description: info.currentStatus.description ?? null,
        occurredAt: info.currentStatus.occurredAt ?? null,
      }
      : (hasTrackingNumber
        ? {
          code: 'ORD',
          name: 'Order Placed',
          category: 'PRE_TRANSIT',
          description: 'Your order has been placed and a tracking number has been created.',
          occurredAt: info.shipmentCreatedAt ?? null,
        }
        : null);

    // New shipmentMap contract: origin -> currentLocation -> destination,
    // city/state/country/pincode only (no latitude/longitude).
    const originStop = normalizeMapStop(map.origin);
    const currentStop = normalizeMapStop(map.currentLocation);
    const destinationStop = normalizeMapStop(map.destination);

    return {
      trackingNumber: info.trackingNumber ?? null,
      shipmentId: info.shipmentId ?? null,
      orderId: info.orderId ?? null,
      orderNumber: info.orderNumber ?? null,
      serviceType: info.serviceType ?? null,
      carrier: info.carrier ?? null,
      currentStatus: mappedCurrentStatus,
      estimatedDeliveryAt: info.estimatedDeliveryAt ?? null,
      deliveredAt: null,
      lastUpdatedAt: mappedCurrentStatus?.occurredAt ?? info.currentStatus?.occurredAt ?? null,
      package: info.package ?? null,
      currentLocation: currentStop,
      customer: info.customer ?? null,
      // Full recipient address for the details panel; the map keeps using the
      // city-level destination stop.
      deliveryAddress: info.deliveryAddress
        ? { ...destinationStop, ...info.deliveryAddress }
        : destinationStop,
      shipmentMap: {
        origin: originStop,
        currentLocation: currentStop,
        destination: destinationStop,
      },
      journey: {
        origin: originStop,
        destination: destinationStop,
        currentLocation: currentStop,
      },
      progress: withOrderPlacedProgress(
        progress.map((step) => ({
          code: step.code ?? null,
          name: step.name ?? step.label ?? null,
          label: step.name ?? step.label ?? null,
          order: step.order ?? null,
          status: Boolean(step.status),
          state: step.state
            || (step.status ? 'COMPLETED' : 'UPCOMING'),
        })),
        mappedCurrentStatus,
      ),
      timeline: timeline.map((event) => normalizeTimelineEvent(event)),
      metadata: { source: 'production-tracking' },
    };
  }

  return {
    ...data,
    timeline: Array.isArray(data.timeline) ? data.timeline.map((event) => normalizeTimelineEvent(event)) : [],
    progress: Array.isArray(data.progress) ? data.progress : [],
  };
}

function withOrderPlacedProgress(progress, currentStatus) {
  if (String(currentStatus?.code || '').toUpperCase() !== 'ORD')
    return progress;
  const hasCurrent = progress.some((step) => String(step.state || '').toUpperCase() === 'CURRENT');
  if (hasCurrent)
    return progress;

  const next = progress.map((step) => (
    String(step.code || '').toUpperCase() === 'ORD'
      ? { ...step, state: 'CURRENT', status: true }
      : step
  ));
  if (next.some((step) => String(step.code || '').toUpperCase() === 'ORD'))
    return next;
  return [
    {
      code: 'ORD',
      name: 'Order Placed',
      label: 'Order Placed',
      order: 10,
      status: true,
      state: 'CURRENT',
    },
    ...next,
  ];
}

/**
 * Normalizes a backend shipmentMap stop into the location record shape the
 * tracking flow map/movement components expect. Returns null when the stop
 * has no usable location data. City/state/country/pincode only.
 */
function normalizeMapStop(stop) {
  if (!stop || typeof stop !== 'object')
    return null;

  const zipcode = stop.pincode ?? stop.pinCode ?? stop.pin_code ?? stop.postalCode ?? stop.zipcode ?? null;
  const record = {
    label: stop.label ?? null,
    city: stop.city ?? null,
    state: stop.state ?? null,
    country: stop.country ?? null,
    pincode: zipcode,
    zipcode,
  };

  const hasValue = Object.values(record).some((value) => value !== null && value !== undefined && value !== '');
  return hasValue ? record : null;
}

function normalizeTimelineEvent(event) {
  if (!event || typeof event !== 'object')
    return event;

  const group = event.group && typeof event.group === 'object'
    ? {
      code: event.group.code ?? null,
      name: event.group.name ?? null,
      order: event.group.order ?? null,
    }
    : (event.lifecycleGroup
      ? { code: event.lifecycleGroup, name: null, order: null }
      : null);

  const statusCode = event.status?.code ?? event.code ?? null;
  const statusName = event.status?.name ?? event.name ?? null;
  const rawScan = event.rawScan
    ?? (typeof event.scan === 'string' ? event.scan : event.scan?.name)
    ?? null;

  return {
    ...event,
    group,
    lifecycleGroup: group?.code ?? event.lifecycleGroup ?? null,
    code: statusCode,
    name: statusName,
    status: event.status && typeof event.status === 'object'
      ? event.status
      : (statusCode || statusName
        ? {
          code: statusCode,
          name: statusName,
          category: event.category ?? null,
          description: event.description ?? null,
        }
        : null),
    rawStatus: event.rawStatus ?? null,
    rawScan,
    occurredAt: event.occurredAt ?? event.scan?.occurredAt ?? null,
    location: event.location ?? null,
    description: event.description ?? null,
  };
}

export function getTrackingNumberFromPathname(pathname = window.location.pathname) {
  const trimmed = String(pathname || '').replace(/^\/+|\/+$/g, '');
  if (!trimmed || trimmed.includes('/')) return null;

  try {
    return decodeURIComponent(trimmed).trim() || null;
  } catch {
    return trimmed.trim() || null;
  }
}

export function buildTrackingPagePath(trackingNumber) {
  const normalizedTrackingNumber = String(trackingNumber || '').trim();
  if (!normalizedTrackingNumber) return '/';
  return `/${encodeURIComponent(normalizedTrackingNumber)}`;
}

export function readTrackingDeepLink(location = window.location) {
  const params = new URLSearchParams(location.search);
  const trackingNumber = getTrackingNumberFromPathname(location.pathname)
    || params.get('trackingNumber')?.trim();
  if (!trackingNumber) return null;

  return {
    trackingNumber,
    options: {
      shareToken: params.get('shareToken')?.trim(),
      shipraAccessTimestamp: params.get('shipraAccessTimestamp')?.trim(),
      shipraAccessSignature: params.get('shipraAccessSignature')?.trim(),
    },
  };
}

export function writeTrackingPageUrl(trackingNumber, { mode = 'replace' } = {}) {
  const url = new URL(window.location.href);
  if (trackingNumber) {
    url.pathname = buildTrackingPagePath(trackingNumber);
    url.searchParams.delete('trackingNumber');
    url.searchParams.delete('shipraAccessTimestamp');
    url.searchParams.delete('shipraAccessSignature');
  } else {
    url.pathname = '/';
    url.search = '';
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next === current) return;

  if (mode === 'push') {
    window.history.pushState({}, '', next);
    return;
  }

  window.history.replaceState({}, '', next);
}

export function buildTrackingShareUrl(trackingNumber) {
  if (!trackingNumber) return null;
  return new URL(buildTrackingPagePath(trackingNumber), window.location.origin).toString();
}
